import type { PayloadError } from 'relay-runtime';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuid } from 'uuid';

export type ErrorMeta = {
	operationName: string;
	requestId?: string;
};

interface PayloadExtensions {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[key: string]: any;
}

export type AGGError = PayloadError & {
	path?: string[];
	extensions?: PayloadExtensions;
};

export type ErrorWrapper = {
	errors: AGGError[];
	meta: ErrorMeta;
};

const errorCollector = new Map<string, ErrorWrapper[]>();

export const CLEAN_UP_TIMEOUT = 120 * 1000; // 2min

const scheduleCleanup = (key: string) => {
	setTimeout(() => {
		errorCollector.delete(key);
	}, CLEAN_UP_TIMEOUT);
};

// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
export const startCaptureGraphQlErrors = (key: string = uuid()) => {
	errorCollector.set(key, []);
	scheduleCleanup(key);
	return key;
};

export const stopCaptureGraphQlErrors = (key: string) => {
	const errors = errorCollector.get(key);
	errorCollector.delete(key);
	return errors || [];
};

export const captureErrors = (errors: AGGError[] | null, meta: ErrorMeta) => {
	if (errors) {
		errorCollector.forEach((value, key) => {
			errorCollector.set(key, [...value, { errors, meta }]);
		});
	}
};

export const isUnauthenticated = (errors: AGGError[]): boolean => {
	if (errors.length > 0) {
		return errors.some((error) => error.message.startsWith('Auth category: UNAUTHENTICATED'));
	}
	return false;
};

/**
 * Those central schema services with external-user-security: true in their config.yaml may encounter this error
 * and this error will happen for all AGG queries/mutations
 *
 * More context in here https://atlassian.slack.com/archives/C01E2MPPLE4/p1764894999989579
 */
export const isStepUpAuthError = (
	errors: AGGError[],
): { hasStepUpAuthError: boolean; stepUpUrl?: string } => {
	const stepUpError = errors.find(
		(error) =>
			error.message.startsWith('Step-up authentication is required.') &&
			error.extensions?.statusCode === 303 &&
			error.extensions?.stepUpUrl,
	);

	return {
		hasStepUpAuthError: !!stepUpError,
		stepUpUrl: stepUpError?.extensions?.stepUpUrl ?? undefined,
	};
};
